import * as THREE from "three";
import AppData from "../../app.data";
import { Loader } from "../../loaders/assets/loader";
import SoundService from "../../services/sound/sound.service";
import { computeVisibleWorldBounds, scaleMeshToMatchHeight } from "../../utils/geometry.utils";
import type { VRInteractionMode, VRViewerAction, VRViewerMedia, VRViewerState } from "./vr.types";

type ViewerRaycastHit = {
  action?: VRViewerAction;
  hitsContent: boolean;
  object: THREE.Object3D;
  point?: THREE.Vector3;
  distance?: number;
};

const CONTENT_DISTANCE = 1.25;
const IMAGE_HEIGHT = 0.9;
const IMAGE_ZOOM_STEP = 0.15;
const MODEL_DEFAULT_HEIGHT = 0.8;
const MODEL_SCALE_MIN = 0.5;
const MODEL_SCALE_MAX = 1.8;
const MODEL_SCALE_STEP = 0.025;
const MODEL_CONTROLLER_ROTATION_SPEED = 18;
const MODEL_MIXER_USER_DATA_KEY = "__artetraAnimationMixers";

const appData = new AppData();

const disposeMaterial = (material: THREE.Material | THREE.Material[] | null | undefined) => {
  if (!material) return;
  if (Array.isArray(material)) {
    material.forEach((entry) => entry?.dispose?.());
    return;
  }
  material.dispose?.();
};

const disposeTexture = (texture: THREE.Texture | null | undefined) => {
  texture?.dispose?.();
};

const createLabelTexture = (label: string) => {
  const canvas = document.createElement("canvas");
  canvas.width = 256;
  canvas.height = 96;
  const context = canvas.getContext("2d");
  if (!context) {
    return null;
  }

  context.fillStyle = "rgba(45, 51, 57, 0.95)";
  context.fillRect(0, 0, canvas.width, canvas.height);
  context.strokeStyle = "#10B981";
  context.lineWidth = 4;
  context.strokeRect(2, 2, canvas.width - 4, canvas.height - 4);
  context.fillStyle = "#E5E7EB";
  context.font = "bold 30px sans-serif";
  context.textAlign = "center";
  context.textBaseline = "middle";
  context.fillText(label, canvas.width / 2, canvas.height / 2);

  const texture = new THREE.CanvasTexture(canvas);
  texture.needsUpdate = true;
  texture.colorSpace = THREE.SRGBColorSpace;
  return texture;
};

const createButtonMesh = (label: string, action: VRViewerAction, position: THREE.Vector3) => {
  const geometry = new THREE.PlaneGeometry(0.24, 0.08);
  const texture = createLabelTexture(label);
  const materialOptions: THREE.MeshBasicMaterialParameters = {
    color: 0xffffff,
    transparent: true,
  };
  if (texture) {
    materialOptions.map = texture;
  }
  const material = new THREE.MeshBasicMaterial(materialOptions);
  const mesh = new THREE.Mesh(geometry, material);
  mesh.position.copy(position);
  mesh.userData.vrViewerAction = action;
  mesh.userData.isVRViewerButton = true;
  return mesh;
};

export class VRViewerManager {
  private readonly scene: THREE.Scene;
  private readonly camera: THREE.Camera;
  private readonly soundService: SoundService;
  private readonly loader: Loader;

  private root: THREE.Group | null = null;
  private contentRoot: THREE.Group | null = null;
  private contentObject: THREE.Object3D | null = null;
  private buttonMeshes: THREE.Mesh[] = [];
  private videoElement: HTMLVideoElement | null = null;
  private videoTexture: THREE.VideoTexture | null = null;
  private imageTexture: THREE.Texture | null = null;
  private currentMedia: VRViewerMedia | null = null;
  private state: VRViewerState = { kind: null, status: "idle", interactionEnabled: false, error: null };
  private pausedBackgroundForVideo = false;
  private defaultScale = new THREE.Vector3(1, 1, 1);
  private viewerMixers: THREE.AnimationMixer[] = [];
  private modelInteraction:
    | {
        controller: THREE.Object3D;
        lastPosition: THREE.Vector3;
      }
    | null = null;
  private readonly onFailure?: (reason: string) => void;

  constructor(scene: THREE.Scene, camera: THREE.Camera, soundService: SoundService, onFailure?: (reason: string) => void) {
    this.scene = scene;
    this.camera = camera;
    this.soundService = soundService;
    this.loader = new Loader();
    this.onFailure = onFailure;
  }

  public getState(): VRViewerState {
    return {
      ...this.state,
      error: this.state.error || null,
    };
  }

  public hasActiveViewer() {
    return Boolean(this.root && this.state.kind);
  }

  public async open(media: VRViewerMedia, interactionMode: VRInteractionMode): Promise<VRViewerState> {
    this.close();

    this.currentMedia = media;
    this.state = {
      kind: media.kind,
      status: "loading",
      artworkId: media.artworkId,
      title: media.title,
      interactionEnabled: false,
      error: null,
    };

    this.root = new THREE.Group();
    this.root.name = "VRViewerRoot";
    this.contentRoot = new THREE.Group();
    this.contentRoot.name = "VRViewerContentRoot";
    this.root.add(this.contentRoot);
    this.scene.add(this.root);
    this.positionRootInFrontOfCamera();

    try {
      if (media.kind === "image") {
        await this.buildImageViewer(media);
        this.addButtons(["close", "zoom-in", "zoom-out", "reset"]);
      } else if (media.kind === "video") {
        await this.buildVideoViewer(media);
        this.addButtons(["close", "play-pause", "mute", "restart"]);
      } else if (media.kind === "3dmodel") {
        await this.buildModelViewer(media);
        this.addButtons(["close", "reset"]);
      }

      this.state = {
        ...this.state,
        status: "ready",
        interactionEnabled: interactionMode === "controller",
      };
      return this.getState();
    } catch (error) {
      console.error("VRViewerManager: failed to open viewer", error);
      this.releaseTransientMedia();
      this.disposeRoot();
      this.state = {
        ...this.state,
        status: "error",
        interactionEnabled: false,
        error: error instanceof Error ? error.message : "Viewer unavailable",
      };
      return this.getState();
    }
  }

  public close() {
    this.stopModelInteraction();
    this.releaseTransientMedia();
    this.disposeRoot();

    this.state = {
      kind: null,
      status: "idle",
      interactionEnabled: false,
      error: null,
    };
  }

  public raycast(origin: THREE.Vector3, direction: THREE.Vector3): ViewerRaycastHit | null {
    if (!this.root) {
      return null;
    }

    const targets = this.getInteractiveObjects();
    if (targets.length === 0) {
      return null;
    }

    const raycaster = new THREE.Raycaster(origin, direction.clone().normalize());
    const intersects = raycaster.intersectObjects(targets, true);
    const hit = intersects[0];
    if (!hit?.object) {
      return null;
    }

    let currentObject: THREE.Object3D | null = hit.object;
    while (currentObject) {
      if (currentObject.userData?.vrViewerAction) {
        return {
          action: currentObject.userData.vrViewerAction as VRViewerAction,
          hitsContent: false,
          object: currentObject,
          point: hit.point.clone(),
          distance: hit.distance,
        };
      }

      if (currentObject.userData?.isVRViewerContent) {
        return {
          hitsContent: true,
          object: currentObject,
          point: hit.point.clone(),
          distance: hit.distance,
        };
      }

      currentObject = currentObject.parent;
    }

    return null;
  }

  public handleAction(action: VRViewerAction) {
    if (action === "close") {
      this.close();
      return;
    }

    if (!this.contentRoot) {
      return;
    }

    if (action === "zoom-in") {
      this.applyScalarZoom(1 + IMAGE_ZOOM_STEP);
      return;
    }
    if (action === "zoom-out") {
      this.applyScalarZoom(1 - IMAGE_ZOOM_STEP);
      return;
    }
    if (action === "reset") {
      this.resetContentTransform();
      return;
    }
    if (action === "play-pause" && this.videoElement) {
      if (this.videoElement.paused) {
        void this.requestVideoPlay("Failed to play video");
      } else {
        this.videoElement.pause();
      }
      return;
    }
    if (action === "mute" && this.videoElement) {
      this.videoElement.muted = !this.videoElement.muted;
      return;
    }
    if (action === "restart" && this.videoElement) {
      this.videoElement.currentTime = 0;
      void this.requestVideoPlay("Failed to restart video");
    }
  }

  public beginModelInteraction(controller: THREE.Object3D) {
    if (this.currentMedia?.kind !== "3dmodel" || !this.contentObject) {
      return;
    }

    const lastPosition = new THREE.Vector3();
    controller.getWorldPosition(lastPosition);
    this.modelInteraction = { controller, lastPosition };
  }

  public stopModelInteraction() {
    this.modelInteraction = null;
  }

  public update(delta: number, options: { interactionMode: VRInteractionMode; scaleInput?: number }) {
    if (this.currentMedia?.kind === "3dmodel" && this.contentObject) {
      if (this.modelInteraction) {
        const currentPosition = new THREE.Vector3();
        this.modelInteraction.controller.getWorldPosition(currentPosition);
        const deltaPosition = currentPosition.clone().sub(this.modelInteraction.lastPosition);
        this.contentObject.rotation.y += deltaPosition.x * MODEL_CONTROLLER_ROTATION_SPEED;
        this.contentObject.rotation.x += deltaPosition.y * MODEL_CONTROLLER_ROTATION_SPEED;
        this.modelInteraction.lastPosition.copy(currentPosition);
      } else if (options.interactionMode === "reticle") {
        this.contentObject.rotation.y += delta * 0.4;
      }

      if (Number.isFinite(options.scaleInput) && Math.abs(options.scaleInput || 0) >= 0.1) {
        const nextScalar = THREE.MathUtils.clamp(
          this.contentRoot.scale.x + (options.scaleInput || 0) * MODEL_SCALE_STEP,
          this.defaultScale.x * MODEL_SCALE_MIN,
          this.defaultScale.x * MODEL_SCALE_MAX
        );
        this.contentRoot.scale.setScalar(nextScalar);
      }
    }
  }

  private getInteractiveObjects() {
    const targets: THREE.Object3D[] = [];
    this.buttonMeshes.forEach((mesh) => targets.push(mesh));
    if (this.contentObject) {
      targets.push(this.contentObject);
    }
    return targets;
  }

  private positionRootInFrontOfCamera() {
    if (!this.root) return;

    const cameraPosition = new THREE.Vector3();
    const forward = new THREE.Vector3();
    this.camera.getWorldPosition(cameraPosition);
    this.camera.getWorldDirection(forward);
    const targetPosition = cameraPosition.clone().add(forward.multiplyScalar(CONTENT_DISTANCE));
    this.root.position.copy(targetPosition);
    this.root.lookAt(cameraPosition);
  }

  private addButtons(actions: VRViewerAction[]) {
    if (!this.root) return;

    const labelMap: Record<VRViewerAction, string> = {
      close: "Close",
      "zoom-in": "Zoom In",
      "zoom-out": "Zoom Out",
      reset: "Reset",
      "play-pause": "Play/Pause",
      mute: "Mute",
      restart: "Restart",
    };

    const width = Math.max(actions.length - 1, 1) * 0.14;
    actions.forEach((action, index) => {
      const mesh = createButtonMesh(
        labelMap[action],
        action,
        new THREE.Vector3((index * 0.28) - width, -0.7, 0)
      );
      this.buttonMeshes.push(mesh);
      this.root?.add(mesh);
    });
  }

  private applyScalarZoom(multiplier: number) {
    if (!this.contentRoot) return;
    const nextScalar = THREE.MathUtils.clamp(this.contentRoot.scale.x * multiplier, 0.45, 2.4);
    this.contentRoot.scale.setScalar(nextScalar);
  }

  private resetContentTransform() {
    if (!this.contentRoot || !this.contentObject) return;
    this.contentRoot.scale.copy(this.defaultScale);
    this.contentObject.rotation.set(0, 0, 0);
  }

  private async buildImageViewer(media: VRViewerMedia) {
    if (!media.imageUrl || !this.contentRoot) {
      throw new Error("Missing image URL");
    }

    const texture = await this.loader.loadTexture(media.imageUrl);
    this.imageTexture = texture;
    const width = texture.image?.width || 1;
    const height = texture.image?.height || 1;
    const aspect = width / Math.max(height, 1);
    const planeGeometry = new THREE.PlaneGeometry(IMAGE_HEIGHT * aspect, IMAGE_HEIGHT);
    const planeMaterial = new THREE.MeshBasicMaterial({
      color: 0xffffff,
      map: texture,
      side: THREE.DoubleSide,
    });
    const plane = new THREE.Mesh(planeGeometry, planeMaterial);
    plane.userData.isVRViewerContent = true;
    this.contentRoot.add(plane);
    this.contentObject = plane;
    this.defaultScale.copy(this.contentRoot.scale);
  }

  private async buildVideoViewer(media: VRViewerMedia) {
    if (!media.videoUrl || !this.contentRoot) {
      throw new Error("Missing video URL");
    }

    const video = document.createElement("video");
    video.crossOrigin = "anonymous";
    video.src = media.videoUrl;
    video.muted = true;
    video.loop = true;
    video.playsInline = true;
    video.preload = "auto";
    video.load();

    await new Promise<void>((resolve, reject) => {
      video.onloadeddata = () => resolve();
      video.onerror = () => reject(new Error("Failed to load video"));
    });

    this.soundService.pauseBackgroundSound();
    this.pausedBackgroundForVideo = true;
    await video.play();

    this.videoElement = video;
    this.videoTexture = new THREE.VideoTexture(video);
    this.videoTexture.colorSpace = THREE.SRGBColorSpace;
    const width = video.videoWidth || 1;
    const height = video.videoHeight || 1;
    const aspect = width / Math.max(height, 1);
    const planeGeometry = new THREE.PlaneGeometry(IMAGE_HEIGHT * aspect, IMAGE_HEIGHT);
    const planeMaterial = new THREE.MeshBasicMaterial({
      color: 0xffffff,
      map: this.videoTexture,
      side: THREE.DoubleSide,
    });
    const plane = new THREE.Mesh(planeGeometry, planeMaterial);
    plane.userData.isVRViewerContent = true;
    this.contentRoot.add(plane);
    this.contentObject = plane;
    this.defaultScale.copy(this.contentRoot.scale);
  }

  private async buildModelViewer(media: VRViewerMedia) {
    if (!media.modelSource || !this.contentRoot) {
      throw new Error("Missing model source");
    }

    const model = await this.loader.load3DModel(media.modelSource, false);
    if (!model) {
      throw new Error("Failed to load model");
    }

    scaleMeshToMatchHeight(model, this.resolveModelDisplayHeight(media));
    model.updateMatrixWorld(true);
    const bounds = computeVisibleWorldBounds(model);
    const center = new THREE.Vector3();
    bounds.getCenter(center);
    model.position.sub(center);
    model.updateMatrixWorld(true);
    const groundedBounds = computeVisibleWorldBounds(model);
    model.position.y -= groundedBounds.min.y;
    model.traverse((child: any) => {
      child.userData.isVRViewerContent = true;
    });
    model.userData.isVRViewerContent = true;
    this.applyVRModelPerformanceProfile(model);
    this.viewerMixers = Array.isArray((model.userData as any)?.[MODEL_MIXER_USER_DATA_KEY])
      ? [...(model.userData as any)[MODEL_MIXER_USER_DATA_KEY]]
      : [];
    this.contentRoot.add(model);
    this.contentObject = model;
    this.defaultScale.copy(this.contentRoot.scale);
  }

  private resolveModelDisplayHeight(media: VRViewerMedia) {
    const height = Number(media.modelDisplayHeight);
    return Number.isFinite(height) && height > 0 ? height : MODEL_DEFAULT_HEIGHT;
  }

  private applyVRModelPerformanceProfile(model: THREE.Object3D) {
    model.traverse((child: any) => {
      if (!child?.isMesh) return;

      child.castShadow = false;
      child.receiveShadow = false;
      child.frustumCulled = true;

      const materials = Array.isArray(child.material) ? child.material : child.material ? [child.material] : [];
      materials.forEach((material: any) => {
        if (!material) return;
        material.needsUpdate = true;
      });
    });
  }

  private async requestVideoPlay(reason: string) {
    if (!this.videoElement) return;
    try {
      await this.videoElement.play();
    } catch (error) {
      this.handleVideoPlaybackFailure(reason, error);
    }
  }

  private handleVideoPlaybackFailure(reason: string, error: unknown) {
    console.error("VRViewerManager: video playback failed", error);
    this.close();
    this.onFailure?.(reason);
  }

  private releaseTransientMedia() {
    if (this.videoElement) {
      this.videoElement.pause();
      this.videoElement.src = "";
      this.videoElement.load();
      this.videoElement = null;
    }
    if (this.pausedBackgroundForVideo) {
      this.soundService.playBackgroundSound();
      this.pausedBackgroundForVideo = false;
    }

    disposeTexture(this.videoTexture);
    disposeTexture(this.imageTexture);
    this.videoTexture = null;
    this.imageTexture = null;
    this.currentMedia = null;
    this.detachViewerMixers();
  }

  private detachViewerMixers() {
    this.viewerMixers.forEach((mixer) => {
      mixer.stopAllAction?.();
      const mixerIndex = appData.skinnedMeshes.indexOf(mixer as any);
      if (mixerIndex >= 0) {
        appData.skinnedMeshes.splice(mixerIndex, 1);
      }
    });
    this.viewerMixers = [];
  }

  private disposeRoot() {
    this.buttonMeshes.forEach((mesh) => {
      disposeTexture((mesh.material as THREE.MeshBasicMaterial)?.map || null);
      mesh.geometry.dispose?.();
      disposeMaterial(mesh.material);
    });
    this.buttonMeshes = [];

    if (this.contentObject) {
      this.contentObject.traverse((child: any) => {
        if (child?.geometry?.dispose) {
          child.geometry.dispose();
        }
        if (child?.material) {
          if ((child.material as THREE.MeshBasicMaterial).map) {
            disposeTexture((child.material as THREE.MeshBasicMaterial).map);
          }
          disposeMaterial(child.material);
        }
      });
      this.contentObject = null;
    }

    if (this.root) {
      this.scene.remove(this.root);
      this.root.clear();
      this.root = null;
      this.contentRoot = null;
    }
  }
}
